import { apiRequest, ApiResponse } from '../request'
import type { PayoutRecipientKind } from './payout-batch-api'

/**
 * Uma linha do repasse que a cobrança vai gerar — ou já gerou.
 *
 * Vem de endpoint próprio, e não de dentro do detalhe da cobrança, porque a projeção
 * mora no módulo de repasse (que já importa o de cobrança; a volta fecharia um ciclo).
 */
export interface PlannedPayout {
    kind: PayoutRecipientKind
    entityId: number
    name: string | null
    amount: number
    /**
     * De onde o valor vem. É a mesma separação que gera contas a pagar DISTINTAS: o
     * anestesista e o material do mesmo hospital não entram na conta da diária dele.
     */
    origin: 'CLINICAL' | 'ANESTHETIST' | 'SUPPLY'
    label: string
    payoutKey: string
    sources: Array<{ type: string; id: number }>
    /** Conta a pagar já gerada (cobrança paga); `null` enquanto é só projeção. */
    payableId?: number | null
    payableStatus?: string | null
    /**
     * Contas do repasse separado por item, a original primeiro. Ausente quando o
     * repasse é uma conta só (o normal).
     */
    itemPayables?: PlannedItemPayable[]
}

/** Uma conta de um repasse separado por item. */
export interface PlannedItemPayable {
    payableId: number
    status: string
    amount: number
    itemId: number | null
    label: string | null
}

/**
 * Repasses previstos de uma cobrança, por destinatário.
 *
 * A tela não soma os campos da fatia para chegar nisso: a fatia não tem a taxa do
 * anestesista, e o total de material vem sem destinatário. Ver
 * `PayoutOrchestratorService.previewBillingPayouts`.
 */
export async function getBillingPayoutPreview(
    billingId: number
): Promise<ApiResponse<PlannedPayout[]>> {
    return apiRequest<PlannedPayout[]>(
        `financial/payouts/billing/${billingId}/preview`,
        {}
    )
}

/** Troca de executante a projetar, do jeito que o backend a valida. */
export interface PayoutRecipientChange {
    kind: 'PARTNER' | 'PROFESSIONAL'
    previousEntityId: number | null
    nextEntityId: number | null
}

export interface PayoutRedirectPreview {
    /** A troca foi RECUSADA — `reason` traz o motivo, na linguagem do operador. */
    blocked: boolean
    reason: string | null
    redirects: Array<{
        payableId: number
        kind: 'PARTNER' | 'PROFESSIONAL'
        amount: number
        fromName: string | null
        toName: string | null
    }>
}

/**
 * O que vai acontecer com o repasse se o executante do atendimento for trocado.
 *
 * Serve ao aviso ANTES de salvar: a troca move a conta a pagar para quem passou a
 * executar, e é recusada quando o repasse já foi pago. Sem consultar isto, o operador
 * só descobre depois — e, no caso em que dá certo, não descobre.
 */
export async function previewPayoutRedirect(
    billingId: number,
    changes: PayoutRecipientChange[],
    /**
     * Atendimento em edição. A conta a pagar do repasse é por atendimento: sem ele, a
     * cobrança com vários atendimentos do mesmo parceiro não sabe qual conta mover.
     */
    source?: {
        sourceType: 'EXAM' | 'CONSULTATION' | 'PROCEDURE'
        sourceId: number
    }
): Promise<ApiResponse<PayoutRedirectPreview>> {
    return apiRequest<PayoutRedirectPreview>(
        `financial/payouts/billing/${billingId}/redirect-preview`,
        { data: { changes, ...(source ?? {}) } },
        'post'
    )
}
